import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, NavLink, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { FileDown, LogOut } from "lucide-react";
import { useDrill } from "./store/drillStore";
import DrillSheet from "./components/DrillSheet";
import Login from "./components/Login";
import ArrBridge from "./modules/ArrBridge";
import Productivity from "./modules/Productivity";
import UnitEcon from "./modules/UnitEcon";
import Pipeline from "./modules/Pipeline";
import Pnl from "./modules/Pnl";
import DataManager from "./modules/DataManager";

const TABS = [
  { id: "pnl", label: "Planning & P&L", el: <Pnl /> },
  { id: "productivity", label: "Sales Productivity", el: <Productivity /> },
  { id: "unit-economics", label: "Unit Economics", el: <UnitEcon /> },
  { id: "pipeline", label: "Pipeline", el: <Pipeline /> },
  { id: "arr", label: "ARR Bridge", el: <ArrBridge /> },
  { id: "data", label: "Data", el: <DataManager /> },
];

function Header({ onExportPdf, exporting, onLogout }) {
  const location = useLocation();
  return (
    <header className="sticky top-0 z-30 shadow-md" style={{ background: "linear-gradient(90deg, var(--navy), var(--navy2) 60%, var(--navy3))" }}>
      <div className="flex items-center justify-between px-6 pt-4">
        <div className="flex items-center gap-3">
          <div className="logo-mark" />
          <div>
            <div className="text-white font-black tracking-tight leading-none" style={{ fontFamily: "Chivo", fontSize: 17 }}>GTM FP&A Command Center</div>
            <div className="text-[10px] uppercase tracking-[0.2em] mt-1" style={{ color: "var(--teal)" }}>Every number drills to the atomic record</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button className="btn-util" style={{ background: "transparent", borderColor: "rgba(255,255,255,0.25)", color: "#fff" }} onClick={onExportPdf} disabled={exporting} data-testid="btn-export-pdf">
            <FileDown size={13} /> {exporting ? "Rendering…" : "Board snapshot (PDF)"}
          </button>
          <button className="btn-util" style={{ background: "transparent", borderColor: "rgba(255,255,255,0.25)", color: "#fff" }} onClick={onLogout} data-testid="btn-logout">
            <LogOut size={13} /> Logout
          </button>
        </div>
      </div>
      <nav className="flex px-4 mt-2 overflow-x-auto" data-testid="main-tabs">
        {TABS.map((t) => (
          <NavLink key={t.id} to={`/${t.id}${location.search}`} className={({ isActive }) => `tab-btn ${isActive ? "active" : ""}`} data-testid={`tab-${t.id}`}>
            {t.label}
          </NavLink>
        ))}
      </nav>
    </header>
  );
}

function Shell({ onLogout }) {
  const restoreFromURL = useDrill((s) => s.restoreFromURL);
  const [exporting, setExporting] = useState(false);
  useEffect(() => { restoreFromURL(); }, [restoreFromURL]);

  const exportPdf = async () => {
    setExporting(true);
    try {
      const html2canvas = (await import("html2canvas")).default;
      const { jsPDF } = await import("jspdf");
      const el = document.getElementById("module-root");
      const canvas = await html2canvas(el, { scale: 1.5, backgroundColor: "#f5f8fc" });
      const pdf = new jsPDF({ orientation: canvas.width > canvas.height ? "landscape" : "portrait", unit: "px", format: [canvas.width / 1.5, canvas.height / 1.5] });
      pdf.addImage(canvas.toDataURL("image/png"), "PNG", 0, 0, canvas.width / 1.5, canvas.height / 1.5);
      pdf.save("board_snapshot.pdf");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: "var(--bg)" }}>
      <Header onExportPdf={exportPdf} exporting={exporting} onLogout={onLogout} />
      <main id="module-root" className="px-6 lg:px-10 py-8 max-w-[1440px] mx-auto">
        <Routes>
          <Route path="/" element={<Navigate to="/arr" replace />} />
          {TABS.map((t) => <Route key={t.id} path={`/${t.id}`} element={t.el} />)}
        </Routes>
      </main>
      <DrillSheet />
      <Toaster position="bottom-right" richColors />
    </div>
  );
}

export default function App() {
  const [authed, setAuthed] = useState(() => localStorage.getItem("gtm_auth") === "1");
  const logout = () => {
    localStorage.removeItem("gtm_auth");
    setAuthed(false);
  };
  if (!authed) return <Login onSuccess={() => setAuthed(true)} />;
  return (
    <BrowserRouter>
      <Shell onLogout={logout} />
    </BrowserRouter>
  );
}
